import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import { graphql } from "react-relay";
import { readInlineData } from "relay-runtime";

import { article as articleData, section as sectionData } from "~/data";
import type { helpersCheckIsLiveArticle$key } from "~/queries/__generated__/helpersCheckIsLiveArticle.graphql";
import type { helpersGetArticleTitleArticle$key } from "~/queries/__generated__/helpersGetArticleTitleArticle.graphql";
import type { helpersGetPodcastTitlePodcast$key } from "~/queries/__generated__/helpersGetPodcastTitlePodcast.graphql";
import type { helpersGetVideoTitleVideo$key } from "~/queries/__generated__/helpersGetVideoTitleVideo.graphql";

export const getVideoTitle = (reference?: helpersGetVideoTitleVideo$key) => {
  const video = readInlineData(
    graphql`
      fragment helpersGetVideoTitleVideo on Video @inline {
        sections {
          value {
            entityId
          }
        }
      }
    `,
    reference ?? null,
  );

  if (!notEmpty(video)) return;

  const sections = first(video?.sections);
  const sectionIds = (sections?.value ?? []).map(section => section?.entityId);
  const hasSCMPOriginals = sectionIds.includes(sectionData?.scmpOriginals?.entityId);

  return hasSCMPOriginals ? "SCMP Originals" : "Videos";
};

export const getPodcastTitle = (reference?: helpersGetPodcastTitlePodcast$key) => {
  const podcast = readInlineData(
    graphql`
      fragment helpersGetPodcastTitlePodcast on Podcast @inline {
        __typename
      }
    `,
    reference ?? null,
  );

  return podcast?.__typename === "Podcast" ? "PODCASTS" : undefined;
};

export const getArticleTitle = (reference?: helpersGetArticleTitleArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersGetArticleTitleArticle on Article @inline {
        types {
          value {
            entityId
          }
        }
        sections {
          value {
            entityId
          }
        }
      }
    `,
    reference ?? null,
  );

  if (!notEmpty(article)) return;

  const articleType = first(first(article?.types)?.value)?.entityId ?? "";

  if (articleType) {
    switch (articleType) {
      case articleData.types.opinion.entityId:
      case articleData.types.debate.entityId:
      case articleData.types.review.entityId:
      case articleData.types.column.entityId:
      case articleData.types.blogs.entityId:
        return "COMMENT";
      case articleData.types.series.entityId:
        return "SCMP Series";
      case articleData.types.infographic.entityId:
        return "Infographics";
      case articleData.types.live.entityId:
        return "Live";
    }
  }

  const sections = first(article?.sections);
  const sectionIds = (sections?.value ?? []).map(section => section?.entityId);
  const hasInfographic = sectionIds.includes(sectionData?.infographics?.entityId);

  return hasInfographic ? "INFOGRAPHICS" : undefined;
};

export const checkIsLiveArticle = (reference: helpersCheckIsLiveArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersCheckIsLiveArticle on Article @inline {
        flattenTypeIds
        liveArticle {
          status
        }
      }
    `,
    reference,
  );

  const isLiveArticle =
    article.flattenTypeIds?.includes(articleData.types.live.entityUuid) ?? false;
  const isLive = article.liveArticle?.status ?? false;

  return { isLive, isLiveArticle };
};
